import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, MessageCircle, Paperclip } from 'lucide-react';
import {
  SubBar,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { formatDate, formatTime } from '@/lib/dates';
import { useMyChannels, type ChannelSummary } from './api';
import { NewDirectConversation } from './NewDirectConversation';

type Filter = 'all' | 'unread' | 'ack';

/** רשימת הערוצים (מסמך התכנון 9.4). */
export function ChannelsPage() {
  const { data, isPending, isError, refetch } = useMyChannels();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim();
    return (data ?? []).filter((channel) => {
      if (filter === 'unread' && channel.unread === 0) return false;
      if (filter === 'ack' && channel.pending_ack === 0) return false;
      if (term && !channel.name.includes(term)) return false;
      return true;
    });
  }, [data, filter, search]);

  const totalAck = (data ?? []).reduce((sum, channel) => sum + channel.pending_ack, 0);

  return (
    <div className="grid content-start">
      <SubBar title="הודעות" subtitle="הערוצים שלך" />

      <div className="grid gap-4 p-4">
        <div className="grid gap-1">
          <label htmlFor="channel-search" className="sr-only">
            חיפוש ערוץ
          </label>
          <input
            id="channel-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="חיפוש ערוץ"
            className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body"
          />
        </div>

        <NewDirectConversation />

        <div className="flex flex-wrap gap-2">
          <Chip active={filter === 'all'} onClick={() => setFilter('all')}>
            הכל
          </Chip>
          <Chip active={filter === 'unread'} onClick={() => setFilter('unread')}>
            לא נקראו
          </Chip>
          <Chip active={filter === 'ack'} onClick={() => setFilter('ack')}>
            דורש אישור {totalAck > 0 && `(${totalAck})`}
          </Chip>
        </div>

        {isPending && (
          <div className="grid gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {isError && <ErrorState onRetry={() => void refetch()} />}

        {data && visible.length === 0 && (
          <EmptyState
            title={filter === 'all' ? 'אין לך ערוצים' : 'אין ערוצים שמתאימים לסינון'}
            description={
              filter === 'all'
                ? 'כשתצורף לערוץ, הוא יופיע כאן.'
                : 'אפשר לבחור סינון אחר.'
            }
          />
        )}

        <ul className="grid list-none gap-2 p-0">
          {visible.map((channel) => (
            <li key={channel.id}>
              <ChannelRow channel={channel} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ChannelRow({ channel }: { channel: ChannelSummary }) {
  const initials = channel.name.trim().slice(0, 2);
  const hasUnread = channel.unread > 0;

  return (
    <Link
      to={`/messages/${channel.id}`}
      className="flex items-center gap-3 rounded-md border border-line bg-raised p-3 no-underline"
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid size-12 shrink-0 place-items-center rounded-pill font-display text-title',
          channel.is_announcement_only ? 'bg-sign text-on-olive' : 'bg-olive-soft text-ink',
        )}
      >
        {channel.is_announcement_only ? (
          <Megaphone size={20} strokeWidth={1.75} />
        ) : channel.kind === 'direct' ? (
          <MessageCircle size={20} strokeWidth={1.75} />
        ) : (
          initials
        )}
      </span>

      <span className="min-w-0 flex-1 grid gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className={cn('text-title text-ink', hasUnread && 'font-medium')}>
            {channel.name}
          </span>
          {channel.is_announcement_only && (
            <span className="rounded-pill bg-sign px-2 py-0.5 text-label text-on-olive">רשמי</span>
          )}
        </span>

        <span className="truncate text-body-sm text-muted">
          {channel.last_body
            ? `${channel.last_author ? `${channel.last_author}: ` : ''}${channel.last_body}`
            : channel.last_attachments > 0
              ? 'קובץ מצורף'
              : 'אין עדיין הודעות'}
        </span>
      </span>

      <span className="grid shrink-0 justify-items-end gap-1">
        {channel.last_at && (
          <span className="text-label text-muted">{relativeTime(channel.last_at)}</span>
        )}
        {channel.pending_ack > 0 ? (
          <StatusBadge tone="warn">לאישור</StatusBadge>
        ) : (
          hasUnread && (
            <span className="min-w-6 rounded-pill bg-olive px-2 py-0.5 text-center text-label font-medium text-on-olive tabular">
              {channel.unread > 99 ? '99+' : channel.unread}
            </span>
          )
        )}
        {channel.last_attachments > 0 && (
          <Paperclip size={14} strokeWidth={1.75} aria-label="קובץ מצורף" className="text-muted" />
        )}
      </span>
    </Link>
  );
}

/** היום מציגים שעה, אחרת תאריך. */
function relativeTime(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  return sameDay ? formatTime(iso) : formatDate(iso);
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'min-h-11 cursor-pointer rounded-pill border px-4 text-label font-medium',
        active ? 'border-olive bg-olive text-on-olive' : 'border-muted bg-transparent text-ink',
      )}
    >
      {children}
    </button>
  );
}
